import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {clone} from "lodash";
import {Entity} from "./entity";
import {adjustFromTimeZoneSafely} from "../date/date";
import {toTransportCategory, TransportCategory} from "./transport-category";
import {AllocatableLoadMethod, toAllocatableLoadMethod} from "./allocatable-load-method";

export const AllocatableLoadSettingsCodec = t.intersection([
  t.type({
    id: t.string,
    objectId: t.string,
    objectType: t.string,
    effectiveDttm: t.number,
    loadVolume: t.number,
    volumeUnitOfMeasure: t.string,
    linearUnitOfMeasure: t.string,
    method: t.string,
    category: t.string,
  }),
  t.partial({
    loadVolumePct: t.number,
    shortVolume: t.number,
    minContainerLevel: t.number,
    minContainerLactLevel: t.number,
    maxNonPrimaryLevel: t.number,
    endDttm: t.number,
    volumeSpreadFactor: t.number,
    description: t.string,
    displayName: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export type AllocatableLoadSettings = t.TypeOf<typeof AllocatableLoadSettingsCodec>;

@Exclude()
export class AllocatableLoadSettingsEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  objectId!: string;

  @Expose()
  objectType!: string;

  @Expose()
  loadVolume!: number

  @Expose()
  loadVolumePct?: number

  @Expose()
  shortVolume?: number

  @Expose()
  volumeUnitOfMeasure!: string

  @Expose()
  minContainerLevel?: number

  @Expose()
  minContainerLactLevel?: number

  @Expose()
  maxNonPrimaryLevel?: number

  @Expose()
  linearUnitOfMeasure?: string

  @Expose()
  @Transform(getDatabaseCodeTransformer(toAllocatableLoadMethod))
  method!: AllocatableLoadMethod;

  @Expose()
  volumeSpreadFactor?: number

  @Expose()
  @Transform(getDatabaseCodeTransformer(toTransportCategory))
  category!: TransportCategory;

  @Expose()
  description?: string;

  @Expose({name: 'displayName'})
  displayableName?: string;

  @Expose()
  effectiveDttm!: number;

  @Expose()
  endDttm?: number;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'ALLOCATABLE_LOAD_SETTINGS';

  get displayName(): Readonly<string> {
    return this.displayableName ?? "Unknown";
  }

  /**
   * Constructor for AllocatableLoadSettingsEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(plain: AllocatableLoadSettings): AllocatableLoadSettingsEntity {
    return plainToInstance(AllocatableLoadSettingsEntity, plain, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<AllocatableLoadSettings> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(AllocatableLoadSettingsCodec, object);
  }

  asTypeValue(): AllocatableLoadSettings {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(AllocatableLoadSettingsCodec, object);
  }

  clone(): AllocatableLoadSettingsEntity {
    return clone(this);
  }

  isActive = (dttm: number = new Date().getTime()): boolean => {
    const adjustedStartDttm = adjustFromTimeZoneSafely(this.effectiveDttm)?.getTime()
    const adjustedEndDttm = adjustFromTimeZoneSafely(this.endDttm)?.getTime()

    const meetsStartThresh = adjustedStartDttm == null || adjustedStartDttm <= dttm
    const meetsEndThresh = adjustedEndDttm == null || adjustedEndDttm > dttm

    return meetsStartThresh && meetsEndThresh
  }
}

export interface AllocatableLoadSettingsPropagation {
  fields: string[]
}
